import { paidLeaveTypeList } from '@/views/Leave/Leave.constants';
import firebase from '../config/firebase.config';

const database = firebase.firestore();
const leaveTypeMap = {
  'Planned Leave': 'usedPlannedLeave',
  'Sick Leave': 'usedSickLeave',
  'Casual Leave': 'usedCasualLeave',
};
export const leaveBalanceService = {
  // Get leave policy (single source of truth)
  async getLeavePolicy(companyId, year = new Date().getFullYear()) {
    try {
      const policyRef = database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('leavePolicy')
        .doc(year.toString());

      const doc = await policyRef.get();

      if (doc.exists) {
        return doc.data();
      }

      return null;
    } catch (error) {
      console.error('Error fetching leave policy:', error);
      throw error;
    }
  },

  // Get user's leave usage
  async getUserLeaveUsage(userId, companyId, year = new Date().getFullYear()) {
    try {
      const usageRef = database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('leaveUsage')
        .doc(userId)
        .collection('years')
        .doc(year.toString());

      const doc = await usageRef.get();

      if (doc.exists) {
        return doc.data();
      }

      // If no usage document exists, return default
      return {
        userId,
        year: parseInt(year),
        usedPlannedLeave: 0,
        usedSickLeave: 0,
        usedCasualLeave: 0,
        companyId,
      };
    } catch (error) {
      console.error('Error fetching user leave usage:', error);
      throw error;
    }
  },

  // Get user's remaining paid leave balance (calculated)
  async getRemainingPaidLeave(
    userId,
    companyId,
    year = new Date().getFullYear()
  ) {
    try {
      const policy = await this.getLeavePolicy(companyId, year);
      const userUsage = await this.getUserLeaveUsage(userId, companyId, year);

      if (!policy) {
        return {
          totalAllocated: 0,
          used: 0,
          remaining: 0,
          hasPolicy: false,
        };
      }

      const totalAllocated = policy.paidLeaveAllocation || 0;
      const used =
        (userUsage.usedPlannedLeave || 0) +
        (userUsage.usedSickLeave || 0) +
        (userUsage.usedCasualLeave || 0);
      const remaining = totalAllocated - used;

      return {
        totalAllocated,
        used,
        remaining,
        hasPolicy: true,
      };
    } catch (error) {
      console.error('Error calculating remaining paid leave:', error);
      throw error;
    }
  },

  // Check if user has sufficient paid leave balance
  async hasSufficientPaidLeaveBalance(
    userId,
    companyId,
    year = new Date().getFullYear()
  ) {
    try {
      const balance = await this.getRemainingPaidLeave(userId, companyId, year);
      return balance.hasPolicy && balance.remaining > 0;
    } catch (error) {
      console.error('Error checking paid leave balance:', error);
      return false;
    }
  },

  // Initialize user's leave usage document (when they first apply)
  async initializeUserLeaveUsage(
    userId,
    companyId,
    year = new Date().getFullYear()
  ) {
    try {
      const usageRef = database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('leaveUsage')
        .doc(userId)
        .collection('years')
        .doc(year.toString());
      const doc = await usageRef.get();
      if (doc.exists) {
        return;
      }
      const usageData = {
        userId,
        year: parseInt(year),
        usedPlannedLeave: 0,
        usedSickLeave: 0,
        usedCasualLeave: 0,
        companyId,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      };

      await usageRef.set(usageData);
      return usageData;
    } catch (error) {
      console.error('Error initializing user leave usage:', error);
      throw error;
    }
  },

  // Get pending paid leave requests for a user (company leaveRequests - single source)
  async getPendingPaidLeaveRequests(userId, companyId) {
    try {
      const requestsSnapshot = await database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('leaveRequests')
        .where('userId', '==', userId)
        .where('type', 'in', paidLeaveTypeList)
        .where('status', '==', 'Pending')
        .get();

      return requestsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching pending paid leave requests:', error);
      throw error;
    }
  },

  // Get approved leave for a user in a date range (for merging into attendance).
  // startDate, endDate in "DD-MM-YYYY" to match getMembersTimeLog.
  async getApprovedLeaveForUserInDateRange(
    companyId,
    userId,
    startDate,
    endDate
  ) {
    try {
      const requestsSnapshot = await database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('leaveRequests')
        .where('userId', '==', userId)
        .where('status', '==', 'Approved')
        .get();

      const [startD, startM, startY] = startDate.split('-').map(Number);
      const [endD, endM, endY] = endDate.split('-').map(Number);
      const startTs = new Date(startY, startM - 1, startD).getTime();
      const endTs = new Date(endY, endM - 1, endD).getTime();

      const list = [];
      requestsSnapshot.docs.forEach((doc) => {
        const d = doc.data();
        const leaveDate = d.leaveDate; // "YYYY-M-D" or "YYYY-MM-DD"
        if (!leaveDate) return;
        const parts = leaveDate.split('-').map(Number);
        const y = parts[0];
        const m = parts[1];
        const day = parts[2];
        const leaveTs = new Date(y, m - 1, day).getTime();
        if (leaveTs >= startTs && leaveTs <= endTs) {
          const dateKey = `${String(day).padStart(2, '0')}:${String(m).padStart(
            2,
            '0'
          )}:${y}`;
          list.push({ leaveDate: dateKey, type: d.type || 'Leave' });
        }
      });
      return list;
    } catch (error) {
      console.error('Error fetching approved leave for date range:', error);
      throw error;
    }
  },

  // Get effective remaining balance (current balance - pending requests)
  async getEffectiveRemainingPaidLeave(
    userId,
    companyId,
    year = new Date().getFullYear()
  ) {
    try {
      // Get current balance
      const currentBalance = await this.getRemainingPaidLeave(
        userId,
        companyId,
        year
      );

      // Get pending requests
      const pendingRequests = await this.getPendingPaidLeaveRequests(
        userId,
        companyId
      );

      // Calculate effective remaining balance
      const effectiveRemaining =
        currentBalance.remaining - pendingRequests.length;

      return {
        ...currentBalance,
        effectiveRemaining: Math.max(0, effectiveRemaining), // Don't go below 0
        pendingRequests: pendingRequests.length,
        pendingRequestsList: pendingRequests,
      };
    } catch (error) {
      console.error('Error calculating effective remaining paid leave:', error);
      throw error;
    }
  },

  // Deduct paid leave when approved
  async deductPaidLeave(
    userId,
    companyId,
    leaveType,
    year = new Date().getFullYear()
  ) {
    try {
      const usageRef = database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('leaveUsage')
        .doc(userId)
        .collection('years')
        .doc(year.toString());

      // Use transaction to ensure data consistency
      await database.runTransaction(async (transaction) => {
        const usageDoc = await transaction.get(usageRef);

        if (!usageDoc.exists) {
          // Initialize if doesn't exist
          const usageData = {
            userId,
            year: parseInt(year),
            usedPlannedLeave: 0,
            usedSickLeave: 0,
            usedCasualLeave: 0,
            [leaveTypeMap[leaveType]]: 1,
            companyId,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          };
          transaction.set(usageRef, usageData);
        } else {
          // Increment existing usage
          transaction.update(usageRef, {
            [leaveTypeMap[leaveType]]:
              firebase.firestore.FieldValue.increment(1),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          });
        }
      });

      return true;
    } catch (error) {
      console.error('Error deducting paid leave:', error);
      throw error;
    }
  },

  // Reverse paid leave deduction when changing from paid leave to another type
  async reversePaidLeaveDeduction(
    userId,
    companyId,
    leaveType,
    year = new Date().getFullYear()
  ) {
    try {
      const usageRef = database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('leaveUsage')
        .doc(userId)
        .collection('years')
        .doc(year.toString());

      // Use transaction to ensure data consistency
      await database.runTransaction(async (transaction) => {
        const usageDoc = await transaction.get(usageRef);

        if (usageDoc.exists) {
          const currentUsage = usageDoc.data();
          const newUsedPaidLeave = Math.max(
            0,
            (currentUsage[leaveTypeMap[leaveType]] || 0) - 1
          );

          transaction.update(usageRef, {
            [leaveTypeMap[leaveType]]: newUsedPaidLeave,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          });
        }
      });

      return true;
    } catch (error) {
      console.error('Error reversing paid leave deduction:', error);
      throw error;
    }
  },

  // Check if user can change leave type (for updates)
  async canChangeLeaveType(
    userId,
    companyId,
    newType,
    originalType,
    year = new Date().getFullYear()
  ) {
    try {
      // If changing from Paid Leave to another type, always allow (but check if approved)
      if (
        paidLeaveTypeList.includes(originalType) &&
        newType === 'Unpaid Leave'
      ) {
        return { canChange: true, requiresReversal: true };
      }

      // If changing to Paid Leave, check balance
      if (
        paidLeaveTypeList.includes(newType) &&
        originalType === 'Unpaid Leave'
      ) {
        const balance = await this.getEffectiveRemainingPaidLeave(
          userId,
          companyId,
          year
        );
        return {
          canChange: balance.effectiveRemaining > 0,
          requiresReversal: false,
          remainingBalance: balance.effectiveRemaining,
        };
      }

      // If no type change, always allow
      return { canChange: true, requiresReversal: false };
    } catch (error) {
      console.error('Error checking if user can change leave type:', error);
      return { canChange: false, requiresReversal: false };
    }
  },
};
